import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ChangeDetectorRef, AfterViewInit, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CrudFormAssignmentFacade, CrudFormAssignmentMode, CrudFormAssignmentUiState, DEFAULT_CRUD_FORM_ASSIGNMENT_MODE } from './crud-form-assignment.facade';
import { FormGroup, FormsModule } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { COMMON_IMPORTS } from '../../global-misc/common-imports';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { FormModel } from '../../global-data-models/form.model';
import { PatientModel } from '../../global-data-models/patient.model';
import { PatientFormAssignmentStatus } from '../../global-data-models/form-assignment.model';
import { MeService } from '../../global-services/me.service';
import * as QRCode from 'qrcode';
import { GlobalCopyToClipboardComponent } from '../../global-components/global-copy-to-clipboard/copy-to-clipboard.component';
import { TooltipModule } from 'primeng/tooltip';
import { AccordionModule } from 'primeng/accordion';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SplitButtonModule } from 'primeng/splitbutton';
import { ConfirmationService } from 'primeng/api';
import { FormResponseViewerComponent, FormResponseViewerData } from '../../global-components/form-response-viewer/form-response-viewer.component';
import { DownloadService } from '../../global-services/download.service';

@Component({
  selector: 'app-crud-form-assignment',
  imports: [
    ...COMMON_IMPORTS,
    DropdownModule,
    CalendarModule,
    InputNumberModule,
    InputTextareaModule,
    FormsModule,
    GlobalCopyToClipboardComponent,
    TooltipModule,
    AccordionModule,
    ConfirmDialogModule,
    TagModule,
    SplitButtonModule,
    FormResponseViewerComponent,
  ],
  templateUrl: './crud-form-assignment.component.html',
  styleUrls: ['./crud-form-assignment.component.css'],
  providers: [ConfirmationService],
})
export class CrudFormAssignmentComponent implements OnInit, OnDestroy, AfterViewInit {
  private subSink = new Subscription();

  public orgId!: string;
  public patientId!: string;
  public assignmentId?: string;
  public userId!: string;

  public form!: FormGroup;
  public crudMode: CrudFormAssignmentMode = CrudFormAssignmentMode.CREATE;
  public CrudFormAssignmentMode = CrudFormAssignmentMode; // For template access
  public patientInfo?: PatientModel;
  public availableForms: FormModel[] = [];
  public formFillUrls?: CrudFormAssignmentUiState['formFillUrls'];

  public get currentFormAssignmentStatus(): PatientFormAssignmentStatus | string | undefined {
    return this.facade.uiState.formAssignment?.status;
  }

  public statusOptions = [
    { label: 'Pending', value: PatientFormAssignmentStatus.PENDING },
    { label: 'In Progress', value: PatientFormAssignmentStatus.IN_PROGRESS },
    { label: 'Completed', value: PatientFormAssignmentStatus.COMPLETED },
    { label: 'Expired', value: PatientFormAssignmentStatus.EXPIRED },
    { label: 'Cancelled', value: PatientFormAssignmentStatus.CANCELLED },
  ];

  public fillingMethodOptions = [
    { label: 'Self-Complete', value: 'SELF' },
    { label: 'Assisted', value: 'ASSISTED' },
    { label: 'Staff Complete', value: 'STAFF' },
  ];

  // Survey Response Viewer properties
  public showResponseViewer = false;
  public responseViewerData?: FormResponseViewerData;

  // Download menu items for split button
  public downloadMenuItems = [
    {
      label: 'Download as JSON',
      icon: 'pi pi-file-o',
      command: () => this.downloadFormResponsesAsJson(),
    },
  ];

  @ViewChild('qrCodeCanvas', { static: false }) qrCodeCanvas?: ElementRef<HTMLCanvasElement>;

  private onSubmitEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() onSubmit: Observable<void> = this.onSubmitEvent.asObservable();

  constructor(
    private readonly facade: CrudFormAssignmentFacade,
    private readonly route: ActivatedRoute,
    private readonly meService: MeService,
    private readonly cdr: ChangeDetectorRef,
    private readonly confirmationService: ConfirmationService,
    private readonly downloadService: DownloadService,
  ) {
    // Initialize with facade's initial form, but this will be updated in ngOnInit
    this.form = this.facade.uiState.form;
  }

  async ngOnInit(): Promise<void> {
    // Extract route parameters
    this.orgId = this.route.snapshot.paramMap.get('orgId') as string;
    this.patientId = this.route.snapshot.paramMap.get('patientId') as string;
    this.assignmentId = this.route.snapshot.paramMap.get('assignmentId') || undefined;

    // Get crud mode from route data
    const crudModeString = this.route.snapshot.data['crudMode'] as string;
    this.crudMode = CrudFormAssignmentMode[crudModeString as keyof typeof CrudFormAssignmentMode];

    // Get current user ID
    try {
      const meData = await this.meService.getMe();
      if (!meData) {
        console.error('No user data available');
        return;
      }
      this.userId = meData.id;
    } catch (error) {
      console.error('Failed to get user data:', error);
      return;
    }

    // Ensure we have a default crudMode if not provided
    if (!this.crudMode) {
      this.crudMode = DEFAULT_CRUD_FORM_ASSIGNMENT_MODE;
    }

    this.subSink.add(
      this.facade.uiState$.subscribe((state: CrudFormAssignmentUiState) => {
        this.form = state.form;
        this.patientInfo = state.patientInfo;
        this.availableForms = state.availableForms;
        this.formFillUrls = state.formFillUrls;
        this.cdr.detectChanges();
        if (this.formFillUrls?.qr) {
          this.generateQrCode(this.formFillUrls.qr);
        }
      }),
    );

    this.route.params.subscribe((params) => {
      this.orgId = params['orgId'];
      this.patientId = params['patientId'];
      this.assignmentId = params['assignmentId'] || undefined;

      // Re-fetch data when route parameters change
      this.facade.begin(this.orgId, this.patientId, this.userId, this.crudMode, this.assignmentId);
    });
  }

  ngAfterViewInit(): void {
    if (this.formFillUrls?.qr) {
      this.generateQrCode(this.formFillUrls.qr);
    }
  }

  private generateQrCode(url: string): void {
    if (this.qrCodeCanvas) {
      QRCode.toCanvas(this.qrCodeCanvas.nativeElement, url, (error) => {
        if (error) console.error(error);
      });
    }
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  public async onFormSubmit() {
    await this.facade.submit();
    this.onSubmitEvent.emit();
  }

  public onCancel() {
    this.facade.resetForm();
  }

  public getFormOptions() {
    return this.availableForms.map((form) => ({
      label: `[${form.identifier} V${form.version}] : ${form.name}`,
      value: form.id,
    }));
  }

  public setDurationPreset(hours: number) {
    this.form.get('durationHours')?.setValue(hours);
  }

  public calculateAge(dateOfBirth: string): number {
    if (!dateOfBirth) return 0;

    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  }

  public formatDate(dateString: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  public sendEmail(): void {
    if (this.formFillUrls?.email) {
      window.location.href = this.formFillUrls.email;
    }
  }

  public sendRCS(): void {
    if (this.formFillUrls?.link) {
      window.location.href = `sms:?body=${this.formFillUrls.link}`;
    }
  }

  public fillOutNow(): void {
    if (this.formFillUrls?.link) {
      window.open(this.formFillUrls.link, '_blank');
    }
  }

  public getSelectedFormName(): string {
    const formId = this.form.get('formId')?.value;
    if (!formId) return '';

    const selectedForm = this.availableForms.find((form) => form.id === formId);
    return selectedForm ? ` - ${selectedForm.name} [${selectedForm.identifier} V${selectedForm.version}]` : '';
  }

  isExtending = false;
  isDeleting = false;

  isFormExpired(): boolean {
    if (!this.facade.uiState.formAssignment) return false;

    return this.facade.uiState.formAssignment.status === PatientFormAssignmentStatus.EXPIRED;
  }

  isFormCompleted(): boolean {
    if (!this.facade.uiState.formAssignment) return false;

    return this.facade.uiState.formAssignment.status === PatientFormAssignmentStatus.COMPLETED;
  }

  shouldShowFormFillLinks(): boolean {
    return this.crudMode === CrudFormAssignmentMode.READ && !!this.formFillUrls && !this.isFormExpired() && !this.isFormCompleted();
  }

  getFormattedExpiryDate(): string {
    if (!this.facade.uiState.formAssignment?.expiresAt) return '';

    return new Date(this.facade.uiState.formAssignment.expiresAt).toLocaleDateString();
  }

  getFormattedAssignedDate(): string {
    if (!this.facade.uiState.formAssignment?.assignedAt) return '';

    return new Date(this.facade.uiState.formAssignment.assignedAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getFormattedCompletedDate(): string {
    if (!this.facade.uiState.formAssignment?.completedAt) return '';

    return new Date(this.facade.uiState.formAssignment.completedAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getFormattedFirstAccessedDate(): string {
    if (!this.facade.uiState.formAssignment?.firstAccessedAt) return '';

    return new Date(this.facade.uiState.formAssignment.firstAccessedAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getFillingMethodDisplay(): string {
    const fillingMethod = this.form.get('fillingMethod')?.value;
    if (!fillingMethod) return '';

    return fillingMethod;
  }

  getCompletionDuration(): string {
    const assignment = this.facade.uiState.formAssignment;
    if (!assignment?.assignedAt || !assignment?.completedAt) return '';

    const assignedDate = new Date(assignment.assignedAt);
    const completedDate = new Date(assignment.completedAt);
    const durationMs = completedDate.getTime() - assignedDate.getTime();

    const days = Math.floor(durationMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((durationMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }

  viewFormResponses(): void {
    // Get the form definition and response data
    const formId = this.form.get('formId')?.value;
    const responseData = this.form.get('responseData')?.value;

    if (!formId || !responseData) {
      console.warn('No form ID or response data available');
      return;
    }

    // Find the selected form
    const selectedForm = this.availableForms.find((form) => form.id === formId);
    if (!selectedForm || !selectedForm.formData) {
      console.warn('Selected form not found or has no form data');
      return;
    }

    try {
      // Prepare the data for the response viewer
      this.responseViewerData = {
        formDefinition: selectedForm.formData,
        responseData: responseData,
        formName: `${selectedForm.name} [${selectedForm.identifier} V${selectedForm.version}]`,
        assignmentId: this.facade.uiState.formAssignment?.id,
        // Patient information
        patientId: this.patientInfo?.id,
        patientName: this.patientInfo ? `${this.patientInfo.firstName} ${this.patientInfo.lastName}` : undefined,
        // Form metadata
        formId: selectedForm.id,
        formIdentifier: selectedForm.identifier,
        formVersion: selectedForm.version,
        // Completion information
        completedAt: this.facade.uiState.formAssignment?.completedAt,
      };

      // Show the modal
      this.showResponseViewer = true;
    } catch (error) {
      console.error('Error preparing response viewer data:', error);
    }
  }

  closeResponseViewer(): void {
    this.showResponseViewer = false;
    this.responseViewerData = undefined;
  }

  downloadFormResponses(): void {
    // Default to CSV download
    this.downloadFormResponsesAsCsv();
  }

  downloadFormResponsesAsJson(): void {
    const responseData = this.form.get('responseData')?.value;
    if (responseData) {
      const filename = `form-responses-${this.facade.uiState.formAssignment?.id || 'unknown'}.json`;
      this.downloadService.downloadAsJson({ data: responseData, filename });
    }
  }

  downloadFormResponsesAsCsv(): void {
    const responseData = this.form.get('responseData')?.value;
    if (responseData) {
      const filename = `form-responses-${this.facade.uiState.formAssignment?.id || 'unknown'}.csv`;
      this.downloadService.downloadAsCsv({ data: responseData, filename });
    } else {
      console.log('CSV Download failed - no response data available');
    }
  }

  downloadAllFiles(): void {
    // TODO: Implement download all files functionality
    console.log('Download all files clicked');
    // This would download all attached files/uploads from the form responses
    // For now, we'll create a placeholder implementation
    const assignment = this.facade.uiState.formAssignment;
    if (assignment) {
      // Mock creating a zip file with all attachments
      console.log(`Preparing to download all files for assignment: ${assignment.id}`);
      // In a real implementation, this would:
      // 1. Get all file URLs from the response data
      // 2. Create a zip file containing all files
      // 3. Download the zip file
    }
  }

  confirmDeleteAssignment(): void {
    if (!this.facade.uiState.formAssignment || !this.patientInfo) return;

    const patientName = `${this.patientInfo.firstName} ${this.patientInfo.lastName}`;
    const formName = this.getSelectedFormName();

    this.confirmationService.confirm({
      message: `This assignment and any metadata will be lost. Are you sure you want to delete the assignment${formName ? ` for "${formName}"` : ''} for ${patientName}?`,
      header: 'Delete Assignment',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deleteAssignment();
      },
    });
  }

  async deleteAssignment(): Promise<void> {
    if (!this.facade.uiState.formAssignment) return;

    this.isDeleting = true;

    try {
      // Mock the delete operation with a timeout
      await new Promise((resolve) => setTimeout(resolve, 1000));

      console.log('Form assignment deleted (mocked)');
      // TODO: Replace with actual API call when backend is ready
      // await this.facade.deleteFormAssignment();
    } catch (error) {
      console.error('Error deleting form assignment:', error);
    } finally {
      this.isDeleting = false;
    }
  }

  public getStatusSeverity(status: PatientFormAssignmentStatus | string | undefined): 'success' | 'warning' | 'danger' | 'info' | 'secondary' {
    if (!status) return 'secondary';

    switch (status) {
      case PatientFormAssignmentStatus.COMPLETED:
        return 'success';
      case PatientFormAssignmentStatus.IN_PROGRESS:
        return 'info';
      case 'ASSIGNED' as any:
        return 'warning';
      case PatientFormAssignmentStatus.PENDING:
        return 'warning';
      case PatientFormAssignmentStatus.EXPIRED:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  public getStatusDisplayValue(status: PatientFormAssignmentStatus | string | undefined): string {
    if (!status) return 'Unknown';

    switch (status) {
      case PatientFormAssignmentStatus.COMPLETED:
        return 'Completed';
      case PatientFormAssignmentStatus.IN_PROGRESS:
        return 'In Progress';
      case 'ASSIGNED' as any:
        return 'Pending';
      case PatientFormAssignmentStatus.PENDING:
        return 'Pending';
      case PatientFormAssignmentStatus.EXPIRED:
        return 'Expired';
      default:
        return status.toString() || 'Unknown';
    }
  }

  async extendFormExpiry(duration: '1_HOUR' | '1_DAY' | '7_DAYS' | '28_DAYS'): Promise<void> {
    if (!this.facade.uiState.formAssignment) return;

    this.isExtending = true;

    try {
      await this.facade.extendFormExpiry(duration);
    } catch (error) {
      console.error('Error extending form expiry:', error);
    } finally {
      this.isExtending = false;
    }
  }
}
